<template>
  <div class="art-crafts-list-container">
    <h1 class="page-title">手工活动管理</h1>
    <el-card class="content-card">
      <div class="card-header">
        <h2>手工活动列表</h2>
        <div>
          <el-button type="primary" @click="handleAdd">新增手工活动</el-button>
        </div>
      </div>
      <div class="card-content">
        <el-table :data="artCrafts" border stripe style="width: 100%" v-loading="tableLoading">
          <el-table-column prop="title" label="项目标题" />
          <el-table-column prop="max_participants" label="上限人数" width="120" />
          <el-table-column prop="remaining_seats" label="剩余座位" width="180">
            <template #default="scope">
              <div style="display: flex; align-items: center;">
                <el-input-number
                  v-model="scope.row.remaining_seats"
                  :min="0"
                  :max="scope.row.max_participants"
                  :step="1"
                  size="small"
                  :disabled="loading[scope.row.id]"
                  @change="handleRemainingSeatsChange(scope.row)"
                />
                <el-icon v-if="loading[scope.row.id]" style="margin-left: 5px; color: #409EFF;">
                  <Loading />
                </el-icon>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'">
                {{ scope.row.status === 'active' ? '活跃' : '不活跃' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sort_order" label="排序" width="180">
            <template #default="scope">
              <div style="display: flex; align-items: center;">
                <el-input-number
                  v-model="scope.row.sort_order"
                  :min="0"
                  :step="1"
                  size="small"
                  :disabled="loading[scope.row.id]"
                  @change="handleSortChange(scope.row)"
                />
                <el-icon v-if="loading[scope.row.id]" style="margin-left: 5px; color: #409EFF;">
                  <Loading />
                </el-icon>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleEdit(scope.row)">
                编辑
              </el-button>
              <el-button 
                type="danger" 
                size="small" 
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <!-- 分页组件 -->
        <div class="pagination-container" style="margin-top: 20px; text-align: right;">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>
    
    <!-- 新增/编辑手工活动抽屉 -->
    <el-drawer
      v-model="dialogVisible"
      :title="isEdit ? '编辑手工活动' : '新增手工活动'"
      direction="rtl"
      size="70%"
    >
      <el-form :model="form" ref="formRef" :rules="rules" label-width="120px">
        <el-form-item label="项目标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入项目标题" />
        </el-form-item>

        <el-form-item label="封面图" prop="cover_image">
          <el-upload
            class="cover-uploader"
            :action="'/api/upload'"
            :show-file-list="true"
            :on-success="handleCoverUploadSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeImageUpload"
            :limit="1"
            :file-list="coverFileList"
          >
            <img v-if="form.cover_image" :src="form.cover_image" class="cover-image" />
            <el-icon v-else class="cover-uploader-icon"><Plus /></el-icon>
          </el-upload>
        </el-form-item>
        <el-form-item label="项目简介" prop="description">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入项目简介"
          />
        </el-form-item>
        <el-form-item label="滚图" prop="carousel_images">
          <el-upload
            class="carousel-uploader"
            :action="'/api/upload'"
            :show-file-list="true"
            :on-success="handleCarouselUploadSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeImageUpload"
            :on-remove="handleCarouselRemove"
            multiple
            :file-list="carouselFileList"
          >
            <el-button type="primary">
              <el-icon><Plus /></el-icon> 上传滚图
            </el-button>
          </el-upload>
          <div class="carousel-tip">支持多张图片上传，最多上传5张</div>
        </el-form-item>
        <el-form-item label="项目详情" prop="details">
          <div class="editor-container">
            <Toolbar
              style="border-bottom: 1px solid #ccc"
              :editor="editorInstance"
              :default-config="editorConfig"
              :mode="'default'"
            />
            <Editor
              v-model="form.details"
              style="height: 300px; overflow-y: auto"
              :default-config="editorConfig"
              :mode="'default'"
              @onCreated="handleCreated"
              @onChange="handleChange"
            />
          </div>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio label="active">活跃</el-radio>
            <el-radio label="inactive">不活跃</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="上限人数" prop="max_participants">
          <el-input-number 
            v-model="form.max_participants" 
            :min="1" 
            :step="1" 
            placeholder="请输入上限人数"
          />
        </el-form-item>
        <el-form-item label="剩余座位" prop="remaining_seats">
          <el-input-number 
            v-model="form.remaining_seats" 
            :min="0" 
            :step="1" 
            placeholder="请输入剩余座位"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number 
            v-model="form.sort_order" 
            :min="0" 
            :step="1" 
            placeholder="请输入排序号"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-drawer>
    

  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
// eslint-disable-next-line no-unused-vars
import { Loading } from '@element-plus/icons-vue';
import request from '../../api/axios';
// eslint-disable-next-line no-unused-vars
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
// 导入wangEditor样式
import '@wangeditor/editor/dist/css/style.css';

// 手工活动列表数据
const artCrafts = ref([]);
// 表格loading状态
const tableLoading = ref(false);
// 分页数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});
// 对话框显示状态
const dialogVisible = ref(false);
// 表单引用
const formRef = ref();
// 是否为编辑模式
const isEdit = ref(false);

// 文件上传相关状态
const coverFileList = ref([]);
const carouselFileList = ref([]);

// 加载状态 - 用于剩余座位编辑
// eslint-disable-next-line no-unused-vars
const loading = reactive({});

// 表单数据
const form = reactive({
  id: '',
  title: '',
  cover_image: '',
  description: '',
  carousel_images: '',
  details: '',
  status: 'active',
  sort_order: 0,
  max_participants: 1,
  remaining_seats: 0
});

// wangEditor配置
// eslint-disable-next-line no-unused-vars
const editorConfig = {
  placeholder: '请输入项目详情',
  MENU_CONF: {
    // 可以添加自定义菜单配置
  }
};
// 编辑器实例，使用ref确保响应式更新
const editorInstance = ref(null);

// 编辑器初始化完成时的回调
// eslint-disable-next-line no-unused-vars
const handleCreated = (editor) => {
  editorInstance.value = editor;
  // 设置初始内容
  editor.setHtml(form.details || '<p></p>');
};

// 编辑器内容变化时的回调
// eslint-disable-next-line no-unused-vars
const handleChange = (editor) => {
  form.details = editor.getHtml();
};

// 监听对话框关闭，销毁编辑器
watch(dialogVisible, (newVal) => {
  if (!newVal && editorInstance.value) {
    editorInstance.value.destroy();
    editorInstance.value = null;
  }
});

// 监听dialogVisible变化，当抽屉打开时初始化编辑器
watch(
  () => dialogVisible.value,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        if (editorInstance.value) {
          editorInstance.value.setHtml(form.details || '<p></p>');
        }
      });
    }
  }
);

// 监听form.details变化，更新编辑器内容
watch(
  () => form.details,
  (newVal) => {
    if (editorInstance.value) {
      editorInstance.value.setHtml(newVal || '<p></p>');
    }
  }
);

// 文件上传相关方法
// 封面图上传成功处理
// eslint-disable-next-line no-unused-vars
const handleCoverUploadSuccess = (response) => {
  if (response.code === 200) {
    form.cover_image = response.data.url;
    coverFileList.value = [{ name: '封面图', url: response.data.url }];
    ElMessage.success('封面图上传成功');
  } else {
    ElMessage.error('封面图上传失败');
  }
};

// 滚图上传成功处理
// eslint-disable-next-line no-unused-vars
const handleCarouselUploadSuccess = (response) => {
  if (response.code === 200) {
    // 确保carousel_images是数组
    if (!Array.isArray(form.carousel_images)) {
      form.carousel_images = [];
    }
    // 添加新的图片URL到数组
    form.carousel_images.push(response.data.url);
    // 更新文件列表
    carouselFileList.value.push({ name: '滚图', url: response.data.url });
    ElMessage.success('滚图上传成功');
  } else {
    ElMessage.error('滚图上传失败');
  }
};

// 上传错误处理
// eslint-disable-next-line no-unused-vars
const handleUploadError = (error) => {
  ElMessage.error('图片上传失败，请重试');
};

// 图片上传前验证
// eslint-disable-next-line no-unused-vars
const beforeImageUpload = (file) => {
  const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
  const isLt2M = file.size / 1024 / 1024 < 2;
  
  if (!isJPG) {
    ElMessage.error('上传图片只能是 JPG/PNG 格式!');
    return false;
  }
  if (!isLt2M) {
    ElMessage.error('上传图片大小不能超过 2MB!');
    return false;
  }
  
  // 限制滚图数量最多5张
  if (carouselFileList.value.length >= 5) {
    ElMessage.error('滚图最多只能上传5张!');
    return false;
  }
  
  return true;
};

// 滚图删除处理
// eslint-disable-next-line no-unused-vars
const handleCarouselRemove = (file, fileList) => {
  // 从carousel_images数组中移除对应的URL
  if (Array.isArray(form.carousel_images)) {
    form.carousel_images = form.carousel_images.filter(url => url !== file.url);
  }
  // 更新文件列表
  carouselFileList.value = fileList;
  ElMessage.success('滚图删除成功');
};

// 表单验证规则
// eslint-disable-next-line no-unused-vars
const rules = {
  title: [
    { required: true, message: '请输入项目标题', trigger: 'blur' },
    { max: 100, message: '项目标题不能超过100个字符', trigger: 'blur' }
  ],
  cover_image: [
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error('请上传封面图'));
        } else {
          callback();
        }
      },
      trigger: 'change'
    }
  ],
  details: [
    { required: true, message: '请输入项目详情', trigger: 'blur' }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ],
  max_participants: [
    { required: true, message: '请输入上限人数', trigger: 'blur' },
    { 
      validator: (rule, value, callback) => {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          callback(new Error('上限人数必须是数字'));
        } else if (numValue < 1) {
          callback(new Error('上限人数不能小于1'));
        } else {
          callback();
        }
      }, 
      trigger: 'blur' 
    } 
  ],
  remaining_seats: [
    { required: true, message: '请输入剩余座位', trigger: 'blur' },
    { 
      validator: (rule, value, callback) => {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          callback(new Error('剩余座位必须是数字'));
        } else if (numValue < 0) {
          callback(new Error('剩余座位不能小于0'));
        } else {
          // 检查剩余座位是否大于上限人数
          const maxParticipants = Number(form.max_participants);
          if (!isNaN(maxParticipants) && numValue > maxParticipants) {
            callback(new Error('剩余座位不能大于上限人数'));
          } else {
            callback();
          }
        }
      }, 
      trigger: 'blur' 
    } 
  ]
};



// 获取手工活动列表
const fetchArtCrafts = async () => {
  tableLoading.value = true;
  try {
    const response = await request({
      url: '/manage/art-crafts',
      method: 'get',
      params: {
        page: pagination.currentPage,
        per_page: pagination.pageSize
      }
    });
    // 检查响应数据结构，确保返回的是分页数据
    let data = [];
    let total = 0;
    if (response && response.data) {
      // 如果是分页数据，提取data和total
      if (response.data.data) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
        total = response.data.total || 0;
      } else {
        // 兼容非分页数据
        data = Array.isArray(response.data) ? response.data : [response.data];
        total = data.length;
      }
      // 过滤掉可能的空数据
      data = data.filter(item => item && typeof item === 'object');
    }
    // 使用新数组替换旧数组，避免直接修改
    artCrafts.value = [...data];
    pagination.total = total;
  } catch (error) {
    console.error('获取手工活动列表失败:', error);
    ElMessage.error('获取手工活动列表失败');
    // 确保artCrafts始终是数组
    artCrafts.value = [];
    pagination.total = 0;
  } finally {
    tableLoading.value = false;
  }
};

// 页面加载时获取手工活动列表
onMounted(() => {
  fetchArtCrafts();
});

// 新增手工活动
// eslint-disable-next-line no-unused-vars
const handleAdd = () => {
  isEdit.value = false;
  // 重置表单
  Object.assign(form, {
    id: '',
    title: '',
    cover_image: '',
    description: '',
    carousel_images: [],
    details: '',
    status: 'active',
    sort_order: 0,
    max_participants: 1,
    remaining_seats: 0
  });
  // 重置文件列表
  coverFileList.value = [];
  carouselFileList.value = [];
  dialogVisible.value = true;
};

// 分页处理函数
// eslint-disable-next-line no-unused-vars
const handleSizeChange = (size) => {
  pagination.pageSize = size;
  fetchArtCrafts();
};

// eslint-disable-next-line no-unused-vars
const handleCurrentChange = (current) => {
  pagination.currentPage = current;
  fetchArtCrafts();
};

// 编辑手工活动
// eslint-disable-next-line no-unused-vars
const handleEdit = (row) => {
  isEdit.value = true;
  // 填充表单数据
  const carouselImagesArray = Array.isArray(row.carousel_images) ? row.carousel_images : (row.carousel_images ? row.carousel_images.split(',') : []);
  
  Object.assign(form, {
    id: row.id,
    title: row.title,
    cover_image: row.cover_image,
    description: row.description,
    carousel_images: carouselImagesArray,
    details: row.details || '',
    status: row.status,
    sort_order: row.sort_order || 0,
    max_participants: row.max_participants || 1,
    remaining_seats: row.remaining_seats || 0
  });
  
  // 初始化文件列表
  coverFileList.value = row.cover_image ? [{ name: '封面图', url: row.cover_image }] : [];
  carouselFileList.value = carouselImagesArray.map((url, index) => ({ 
    name: `滚图${index + 1}`, 
    url: url.trim() 
  }));
  
  dialogVisible.value = true;
};

// 删除手工活动
// eslint-disable-next-line no-unused-vars
const handleDelete = (row) => {
  ElMessageBox.confirm('确定要删除该手工活动吗？', '警告', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await request({
        url: `/manage/art-crafts/${row.id}`,
        method: 'delete'
      });
      ElMessage.success('删除手工活动成功');
      fetchArtCrafts();
    } catch (error) {
      ElMessage.error('删除手工活动失败');
    }
  }).catch(() => {
    // 取消删除操作
  });
};

// 处理剩余座位变化
// eslint-disable-next-line no-unused-vars
const handleRemainingSeatsChange = async (row) => {
  // 设置加载状态
  loading[row.id] = true;
  
  try {
    // 确保remaining_seats在有效范围内
    const remainingSeats = Math.max(0, Math.min(row.remaining_seats, row.max_participants));
    
    // 调用API更新剩余座位
    await request({
      url: `/manage/art-crafts/${row.id}`,
      method: 'put',
      data: {
        remaining_seats: remainingSeats
      }
    });
    
    ElMessage.success('剩余座位更新成功');
    
    // 更新本地数据，确保显示正确值
    row.remaining_seats = remainingSeats;
  } catch (error) {
    ElMessage.error('剩余座位更新失败');
    // 恢复原始值
    fetchArtCrafts();
  } finally {
    // 重置加载状态
    loading[row.id] = false;
  }
};

// 处理排序变化
// eslint-disable-next-line no-unused-vars
const handleSortChange = async (row) => {
  // 设置加载状态
  loading[row.id] = true;
  
  try {
    // 确保sort_order是有效的数字
    const sortOrder = Math.max(0, Number(row.sort_order) || 0);
    
    // 调用API更新排序
    await request({
      url: `/manage/art-crafts/${row.id}`,
      method: 'put',
      data: {
        sort_order: sortOrder
      }
    });
    
    ElMessage.success('排序更新成功');
    
    // 更新本地数据，确保显示正确值
    row.sort_order = sortOrder;
  } catch (error) {
    ElMessage.error('排序更新失败');
    // 恢复原始值
    fetchArtCrafts();
  } finally {
    // 重置加载状态
    loading[row.id] = false;
  }
};

// 提交手工活动表单
// eslint-disable-next-line no-unused-vars
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    
    // 处理滚图数据，确保是数组格式
  const formData = {
    ...form,
    carousel_images: Array.isArray(form.carousel_images) ? form.carousel_images : (form.carousel_images ? form.carousel_images.split(',').map(item => item.trim()) : []),
    // 确保sort_order为数字
    sort_order: form.sort_order || 0
  };
    
    if (isEdit.value) {
      // 更新手工活动
      await request({
        url: `/manage/art-crafts/${form.id}`,
        method: 'put',
        data: formData
      });
      ElMessage.success('更新手工活动成功');
    } else {
      // 新增手工活动
      await request({
        url: '/manage/art-crafts',
        method: 'post',
        data: formData
      });
      ElMessage.success('新增手工活动成功');
    }
    
    dialogVisible.value = false;
    fetchArtCrafts();
  } catch (error) {
    if (error.name === 'Error') {
      // 表单验证失败，不显示错误信息
      return;
    }
    ElMessage.error(isEdit.value ? '更新手工活动失败' : '新增手工活动失败');
  }
};

</script>

<style scoped>
.art-crafts-list-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-content {
  padding: 20px 0;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.dialog-footer {
  text-align: right;
}

/* wangEditor样式 */
.editor-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.w-e-text-container {
  max-height: 300px;
  overflow-y: auto;
}


</style>